import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/api";
import "./AddCourse.css";
import BackButton from "../../components/BackButton/BackButton";

// ─── helpers ────────────────────────────────────────────────
const isPlaylistUrl = (url) => /[?&]list=/.test(url);
const isVideoUrl    = (url) => /(?:v=|youtu\.be\/)([^&?/]+)/.test(url);

const fmtDuration = (sec) => {
  if (!sec) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const totalDuration = (videos) =>
  fmtDuration(videos.reduce((a, v) => a + (v.durationSec || 0), 0));

// ─── How-to steps shown before the user pastes anything ────
const HOW_TO_STEPS = [
  {
    icon: "🔗",
    title: "Paste a YouTube link",
    desc: "Works with any YouTube playlist URL or a single video URL.",
    example: "youtube.com/playlist?list=... or youtube.com/watch?v=...",
  },
  {
    icon: "🔍",
    title: "We detect the type",
    desc: "Learnlia automatically figures out if it's a playlist or a single video.",
    example: null,
  },
  {
    icon: "📚",
    title: "Playlist → instant course",
    desc: "Every video in the playlist becomes a lesson. Progress is tracked automatically.",
    example: null,
  },
  {
    icon: "🎬",
    title: "Single video → build your own",
    desc: "Pick related videos to add, reorder them by drag-and-drop, and create a custom course.",
    example: null,
  },
];

// ─── Main Component ─────────────────────────────────────────
export default function AddCourse() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const inputRef  = useRef(null);

  const [url,     setUrl]     = useState(location.state?.playlistUrl || "");
  const [mode,    setMode]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const [submittingPlaylist, setSubmittingPlaylist] = useState(false);

  const [mainVideo,     setMainVideo]     = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [courseVideos,  setCourseVideos]  = useState([]);
  const [courseTitle,   setCourseTitle]   = useState("");
  const [submitting,    setSubmitting]    = useState(false);

  const [addUrl,        setAddUrl]        = useState("");
  const [addUrlLoading, setAddUrlLoading] = useState(false);
  const [addUrlError,   setAddUrlError]   = useState("");

  // Was this page opened from a detached skill? (re-add flow)
  const isReAdd = !!(location.state?.playlistUrl || location.state?.videoUrl);

  const dragIndex = useRef(null);

  useEffect(() => {
    if (url) detect(url);
    // eslint-disable-next-line
  }, []);

  // ── After creating a course, re-link any detached roadmap resources ──
  const relinkIfNeeded = async (courseId) => {
    if (!isReAdd) return;
    try {
      await api.post("/courses/relink", {
        courseId,
        playlistUrl: location.state?.playlistUrl || undefined,
        videoUrl:    location.state?.videoUrl    || undefined,
      });
    } catch (e) {
      console.warn("relink failed (non-fatal):", e.message);
    }
  };

  const reset = () => {
    setMode(null);
    setMainVideo(null);
    setRelatedVideos([]);
    setCourseVideos([]);
    setCourseTitle("");
    setError("");
  };

  const detect = async (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    reset();

    if (isPlaylistUrl(trimmed)) { setMode("playlist"); return; }

    if (isVideoUrl(trimmed)) {
      setMode("video");
      setLoading(true);
      try {
        const res = await api.get("/courses/video-preview", { params: { videoUrl: trimmed } });
        const { video, related } = res.data;
        setMainVideo(video);
        setCourseVideos([{ ...video, order: 0 }]);
        setCourseTitle(video.title);
        setRelatedVideos(related || []);
      } catch (e) {
        setError(e.response?.data?.message || "Failed to fetch video details.");
        setMode(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    setError("Please enter a valid YouTube video or playlist URL.");
  };

  const submitPlaylist = async () => {
    setSubmittingPlaylist(true);
    setError("");
    try {
      const res = await api.post("/courses", { playlistUrl: url });
      await relinkIfNeeded(res.data.course._id);
      if (res.data.wasTruncated) {
        setError(
          "⚠️ This playlist has 300+ videos — only the first 300 were imported. You can add more manually using the video URL builder."
        );
        setTimeout(() => navigate(`/course/${res.data.course._id}`), 3500);
      } else {
        navigate(`/course/${res.data.course._id}`);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to import playlist.");
      setSubmittingPlaylist(false);
    }
  };

  const inCourse = (id) => courseVideos.some((v) => v.videoId === id);

  const addVideo = (video) => {
    if (inCourse(video.videoId)) return;
    setCourseVideos((prev) => [...prev, { ...video, order: prev.length }]);
  };

  const removeVideo = (id) => {
    setCourseVideos((prev) =>
      prev.filter((v) => v.videoId !== id).map((v, i) => ({ ...v, order: i }))
    );
  };

  const moveVideo = (index, dir) => {
    setCourseVideos((prev) => {
      const next = [...prev];
      const swap = index + dir;
      if (swap < 0 || swap >= next.length) return next;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next.map((v, i) => ({ ...v, order: i }));
    });
  };

  const onDragStart = (i) => { dragIndex.current = i; };
  const onDragOver  = (e) => e.preventDefault();
  const onDrop      = (i) => {
    if (dragIndex.current === null || dragIndex.current === i) return;
    setCourseVideos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex.current, 1);
      next.splice(i, 0, moved);
      return next.map((v, idx) => ({ ...v, order: idx }));
    });
    dragIndex.current = null;
  };

  const submitCourse = async () => {
    if (!courseVideos.length) { setError("Add at least one video."); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/courses/add-custom", {
        title:            courseTitle || mainVideo?.title || "My Course",
        thumbnail:        courseVideos[0]?.thumbnail || mainVideo?.thumbnail,
        creatorName:      mainVideo?.channelTitle   || "Custom",
        creatorChannelId: mainVideo?.channelId      || "",
        videos: courseVideos.map((v, i) => ({
          videoId:     v.videoId,
          title:       v.title,
          durationSec: v.durationSec || 0,
          order:       i,
        })),
      });
      await relinkIfNeeded(res.data.course._id);
      navigate(`/course/${res.data.course._id}`);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to create course.");
      setSubmitting(false);
    }
  };

  const fetchAndAddByUrl = async () => {
    const trimmed = addUrl.trim();
    if (!trimmed) return;
    if (!isVideoUrl(trimmed)) { setAddUrlError("Please enter a valid YouTube video URL."); return; }
    setAddUrlLoading(true);
    setAddUrlError("");
    try {
      const res = await api.get("/courses/video-details", { params: { videoUrl: trimmed } });
      const video = res.data.video;
      if (inCourse(video.videoId)) { setAddUrlError("This video is already in your course."); return; }
      addVideo(video);
      setAddUrl("");
    } catch (e) {
      setAddUrlError(e.response?.data?.message || "Could not fetch video.");
    } finally {
      setAddUrlLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────
  return (
    <div className="acp-page">

      {/* Back button */}
      <div className="acp-back">
        <BackButton to="/dashboard" label="Back to Dashboard" />
      </div>

      {/* ── Hero / URL input ────────────────────────────── */}
      <div className={`acp-hero ${mode ? "acp-hero--compact" : ""}`}>
        <div className="acp-hero-text">
          <h1>Add a Course</h1>
          <p>Paste a YouTube <strong>playlist</strong> or <strong>single video</strong> link to get started</p>
        </div>

        <div className={`acp-url-bar ${mode ? `acp-url-bar--${mode}` : ""}`}>
          <div className="acp-url-icon">
            {mode === "playlist" ? "📋" : mode === "video" ? "🎬" : "🔗"}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={url}
            onChange={(e) => { setUrl(e.target.value); if (mode) reset(); }}
            onKeyDown={(e) => e.key === "Enter" && detect(url)}
            placeholder="https://youtube.com/watch?v=... or ?list=..."
            disabled={loading}
          />
          <button
            className="acp-detect-btn"
            onClick={() => detect(url)}
            disabled={loading || !url.trim()}
          >
            {loading ? <span className="acp-spinner" /> : "Detect →"}
          </button>
        </div>

        {mode && (
          <div className={`acp-mode-pill acp-mode-pill--${mode}`}>
            {mode === "playlist" ? "📋 Playlist detected" : "🎬 Single video detected"}
            <button className="acp-mode-clear" onClick={() => { reset(); setUrl(""); }}>✕</button>
          </div>
        )}

        {error && <p className="acp-error">{error}</p>}
      </div>

      {/* ── How it works — shown only before detection ──── */}
      {!mode && !loading && (
        <div className="acp-guide acp-fade-in">
          <h2 className="acp-guide-title">How it works</h2>
          <div className="acp-guide-steps">
            {HOW_TO_STEPS.map((step, i) => (
              <div className="acp-guide-step" key={i}>
                <div className="acp-guide-step-num">{i + 1}</div>
                <div className="acp-guide-icon">{step.icon}</div>
                <div className="acp-guide-text">
                  <strong>{step.title}</strong>
                  <p>{step.desc}</p>
                  {step.example && (
                    <span className="acp-guide-example">{step.example}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quick tip pills */}
          <div className="acp-tips">
            <div className="acp-tips-label">💡 Quick tips</div>
            <div className="acp-tips-row">
              <span className="acp-tip-pill">✅ Progress tracked per video</span>
              <span className="acp-tip-pill">🔥 Watching earns streak minutes</span>
              <span className="acp-tip-pill">📊 Analytics update in real-time</span>
              <span className="acp-tip-pill">🔁 Resume exactly where you stopped</span>
            </div>
          </div>
        </div>
      )}

      {/* ── PLAYLIST FLOW ───────────────────────────────── */}
      {mode === "playlist" && (
        <div className="acp-playlist-confirm acp-fade-in">
          <div className="acp-playlist-icon">📋</div>
          <h2>Import Full Playlist</h2>
          <p>
            We'll fetch every video from this playlist automatically and build
            your structured course with progress tracking.
          </p>

          {/* What you get */}
          <div className="acp-playlist-perks">
            <div className="acp-perk"><span>✅</span> Every video becomes a tracked lesson</div>
            <div className="acp-perk"><span>📈</span> Overall progress % calculated for you</div>
            <div className="acp-perk"><span>🔥</span> Each video watched counts toward your streak</div>
            <div className="acp-perk"><span>▶️</span> Resume exactly where you left off</div>
          </div>

          <button
            className="acp-btn-primary"
            onClick={submitPlaylist}
            disabled={submittingPlaylist}
          >
            {submittingPlaylist
              ? <><span className="acp-spinner" /> Importing…</>
              : "Import Playlist →"}
          </button>
        </div>
      )}

      {/* ── VIDEO BUILDER FLOW ──────────────────────────── */}
      {mode === "video" && !loading && mainVideo && (
        <div className="acp-builder acp-fade-in">

          {/* Inline guide for video mode */}
          <div className="acp-video-guide">
            <span className="acp-video-guide-icon">💡</span>
            <p>
              Your video is added as lesson 1. Browse related videos on the right and click
              <strong> + Add</strong> to include them. Drag items in your list to reorder,
              or paste more URLs directly below the list.
            </p>
          </div>

          {/* Course title */}
          <div className="acp-title-row">
            <label className="acp-label">Course Name</label>
            <input
              className="acp-title-input"
              type="text"
              value={courseTitle}
              onChange={(e) => setCourseTitle(e.target.value)}
              placeholder="Give your course a name…"
            />
          </div>

          <div className="acp-columns">

            {/* ── LEFT: Course Video List ──────────────── */}
            <div className="acp-panel acp-panel--course">
              <div className="acp-panel-header">
                <div>
                  <h3>Your Course</h3>
                  <span className="acp-panel-sub">
                    {courseVideos.length} video{courseVideos.length !== 1 ? "s" : ""}
                    {courseVideos.length > 0 && ` · ${totalDuration(courseVideos)}`}
                  </span>
                </div>
                <span className="acp-drag-hint">⠿ drag to reorder</span>
              </div>

              {courseVideos.length === 0 ? (
                <div className="acp-empty-list">
                  <span>No videos yet</span>
                  <p>Add from related videos →</p>
                </div>
              ) : (
                <ul className="acp-course-list">
                  {courseVideos.map((video, i) => (
                    <li
                      key={video.videoId}
                      className="acp-course-item"
                      draggable
                      onDragStart={() => onDragStart(i)}
                      onDragOver={onDragOver}
                      onDrop={() => onDrop(i)}
                    >
                      <span className="acp-drag-handle">⠿</span>
                      <span className="acp-item-num">{i + 1}</span>
                      <img src={video.thumbnail} alt="" className="acp-item-thumb" loading="lazy" />
                      <div className="acp-item-info">
                        <span className="acp-item-title">{video.title}</span>
                        <span className="acp-item-dur">{fmtDuration(video.durationSec)}</span>
                      </div>
                      <div className="acp-item-controls">
                        <button className="acp-move-btn" onClick={() => moveVideo(i, -1)} disabled={i === 0} title="Move up">↑</button>
                        <button className="acp-move-btn" onClick={() => moveVideo(i, 1)} disabled={i === courseVideos.length - 1} title="Move down">↓</button>
                        <button className="acp-remove-btn" onClick={() => removeVideo(video.videoId)} title="Remove">✕</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Add by URL */}
              <div className="acp-add-by-url">
                <p className="acp-add-url-label">Or paste a video URL directly</p>
                <div className={`acp-add-url-bar ${addUrlLoading ? "acp-add-url-bar--loading" : ""}`}>
                  <span className="acp-add-url-icon">🔗</span>
                  <input
                    type="text"
                    value={addUrl}
                    onChange={(e) => { setAddUrl(e.target.value); setAddUrlError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && fetchAndAddByUrl()}
                    placeholder="Paste a YouTube video URL…"
                    disabled={addUrlLoading}
                  />
                  <button
                    className="acp-add-url-btn"
                    onClick={fetchAndAddByUrl}
                    disabled={addUrlLoading || !addUrl.trim()}
                  >
                    {addUrlLoading ? <span className="acp-spinner" /> : "+ Add"}
                  </button>
                </div>
                {addUrlError && <p className="acp-add-url-error">{addUrlError}</p>}
              </div>

              <button
                className="acp-btn-primary acp-submit-btn"
                onClick={submitCourse}
                disabled={submitting || courseVideos.length === 0}
              >
                {submitting
                  ? <><span className="acp-spinner" /> Creating…</>
                  : `Create Course (${courseVideos.length} video${courseVideos.length !== 1 ? "s" : ""}) →`}
              </button>
            </div>

            {/* ── RIGHT: Related Videos ────────────────── */}
            <div className="acp-panel acp-panel--related">
              <div className="acp-panel-header">
                <div>
                  <h3>Related Videos</h3>
                  <span className="acp-panel-sub">Click + Add to include in your course</span>
                </div>
              </div>

              <div className="acp-related-grid">
                {relatedVideos.length === 0 && (
                  <p className="acp-no-related">No related videos found.</p>
                )}
                {relatedVideos.map((video) => {
                  const added = inCourse(video.videoId);
                  return (
                    <div
                      key={video.videoId}
                      className={`acp-related-card ${added ? "acp-related-card--added" : ""}`}
                    >
                      <div className="acp-related-thumb-wrap">
                        <img src={video.thumbnail} alt="" className="acp-related-thumb" loading="lazy" />
                        {video.durationSec > 0 && (
                          <span className="acp-related-dur">{fmtDuration(video.durationSec)}</span>
                        )}
                      </div>
                      <div className="acp-related-info">
                        <p className="acp-related-title">{video.title}</p>
                        <span className="acp-related-channel">{video.channelTitle}</span>
                      </div>
                      <button
                        className={`acp-add-btn ${added ? "acp-add-btn--added" : ""}`}
                        onClick={() => added ? removeVideo(video.videoId) : addVideo(video)}
                      >
                        {added ? "✓ Added" : "+ Add"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="acp-loading acp-fade-in">
          <div className="acp-spinner acp-spinner--lg" />
          <p>Fetching video details…</p>
        </div>
      )}

    </div>
  );
}